import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {getStore} from 'src/v2/state/store';
import {useIntl} from 'react-intl';
import {usePrepareSessionForPayment} from 'src/v2/components/pay/usePrepareSessionForPayment';
import {useConfirmSessionForPayment} from 'src/v2/components/pay/useConfirmSessionForPayment';
import type {SupportedCurrency} from 'src/v2/state/types';
import {EVM_NETWORKS} from 'src/types';
import {convertWalletTypeToApiType} from 'src/utils/convertWalletTypeToApiType';
import {
  getEvmTransactionData,
  getSolanaTransactionData,
} from 'src/v2/components/pay/getTransactionData';
import {getSourceTokenAccountAddress} from 'src/utils/solanaHelpers';
import {useSignPermitMessage} from 'src/v2/components/pay/useSignPermitMessage';
import {syncWalletToSession} from 'src/v2/components/pay/syncWalletToSession';
import {useUpdateSessionOnWalletConnect} from 'src/v2/components/walletConnect/useUpdateSessionOnWalletConnect';
import {
  handleError,
  clearErrorBanner,
} from 'src/v2/components/error/handleError';
import {useLogger} from 'src/utils/logger';
import {apiAmountToAssetAmount} from 'src/utils/helpers';
import {MAX_UINT_256_VALUE} from 'src/utils/constants';
import {useResyncSessionState} from 'src/v2/components/pay/useResyncSessionState';
import {useSessionContext} from 'src/state/usePayinSessionContext';

export const useHandlePayment = () => {
  const intl = useIntl();
  const walletClient = useWalletConnectionClient();
  const store = getStore();
  const prepareSessionForPayment = usePrepareSessionForPayment();
  const confirmSessionForPayment = useConfirmSessionForPayment();
  const {signPermitMessage} = useSignPermitMessage();
  const [updateSession] = useUpdateSessionOnWalletConnect();
  const logger = useLogger();
  const resyncSessionState = useResyncSessionState();
  const {sessionId} = useSessionContext();

  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const amount = store.useSelector((state) => state.paymentDetails.amount);
  const paymentCurrency = store.useSelector(
    (state) => state.paymentDetails.currency,
  );
  const depositAddresses = store.useSelector((state) => state.depositAddresses);
  const walletSessions = store.useSelector((state) => state.walletSessions);
  const setupFutureUsage = store.useSelector((state) => state.setupFutureUsage);
  const enableGaslessV2 = store.useSelector(
    (state) => state.featureFlags.enable_gasless_v2,
  );
  const spenderContractAddresses = store.useSelector(
    (state) => state.spenderContractAddresses,
  );

  const handlePayment = async ({
    selectedCurrency,
    setIsProcessing,
    onConfirmSuccess,
  }: {
    selectedCurrency: SupportedCurrency;
    setIsProcessing: (processing: boolean) => void;
    onConfirmSuccess: () => void;
  }): Promise<void> => {
    if (!activeWallet) {
      return;
    }

    clearErrorBanner();

    logger.log('crypto_payin_session.pay_button_pressed', {
      currency: selectedCurrency.id,
      provider: activeWallet.type,
      address: activeWallet.address,
      wallet_key: activeWallet.key,
    });

    const isEvm = EVM_NETWORKS.includes(selectedCurrency.currencyNetwork);
    const isGasless =
      isEvm &&
      enableGaslessV2 &&
      !activeWallet.isSmartContractWallet &&
      !!spenderContractAddresses?.[
        `${selectedCurrency.assetCode}.${selectedCurrency.currencyNetwork}`
      ];

    try {
      setIsProcessing(true);

      await syncWalletToSession(activeWallet, walletSessions, updateSession);
      const walletProvider = convertWalletTypeToApiType(activeWallet.type);

      const {transactionDetails} = await prepareSessionForPayment({
        selectedCurrency: selectedCurrency.assetCode,
        walletAddress: activeWallet.address,
        network: selectedCurrency.currencyNetwork,
        walletProvider,
      });

      if (isEvm) {
        if (isGasless || setupFutureUsage) {
          store.setValue((state) => {
            state.overlay = 'CONFIRM_PAYMENT_WITH_FUTURE_USAGE';
          });

          // For SFU payments, request the maximum allowance because the payment amount can change in the future
          const permitValue = setupFutureUsage
            ? MAX_UINT_256_VALUE
            : apiAmountToAssetAmount(
                amount,
                paymentCurrency,
                selectedCurrency.currencyMinorUnits,
              );

          const signature = await signPermitMessage({
            asset: selectedCurrency,
            permitValue,
          });

          store.setValue((state) => {
            state.overlay = null;
          });

          await confirmSessionForPayment({
            signedTransactionData: signature,
            selectedCurrency: selectedCurrency.assetCode,
            walletAddress: activeWallet.address,
            network: selectedCurrency.currencyNetwork,
            walletProvider,
            walletKey: activeWallet.key,
          });
        } else {
          const transactionData = getEvmTransactionData({
            selectedCurrency,
            amount,
            depositAddresses,
            memoMessage: sessionId,
            sourceCurrency: paymentCurrency,
          });

          store.setValue((state) => {
            state.overlay = 'CONFIRM_PAYMENT';
          });

          // Transaction hash is guaranteed to be a valid transaction hash
          const transactionHash = await walletClient.evmSendTransaction(
            transactionData,
          );

          store.setValue((state) => {
            state.overlay = null;
          });

          await confirmSessionForPayment({
            selectedCurrency: selectedCurrency.assetCode,
            walletAddress: activeWallet.address,
            network: selectedCurrency.currencyNetwork,
            walletProvider,
            walletKey: activeWallet.key,
            transactionHash,
          });
        }
      } else {
        const transactionData = getSolanaTransactionData({
          selectedCurrency,
          amount,
          depositAddresses,
          memoMessage: sessionId,
          transactionDetails,
          sourceCurrency: paymentCurrency,
        });

        store.setValue((state) => {
          state.overlay = 'CONFIRM_PAYMENT';
        });

        const transactionHash = await walletClient.solanaSignTransaction(
          transactionData,
        );

        store.setValue((state) => {
          state.overlay = null;
        });

        const solanaSourceTokenAccountAddress = activeWallet.address
          ? getSourceTokenAccountAddress(
              selectedCurrency.contractAddress,
              activeWallet.address,
              selectedCurrency.assetCode.toString(),
            )
          : undefined;

        await confirmSessionForPayment({
          signedTransactionData: transactionHash,
          solanaSourceTokenAccountAddress:
            solanaSourceTokenAccountAddress?.toBase58(),
          selectedCurrency: selectedCurrency.assetCode,
          walletAddress: activeWallet.address,
          network: selectedCurrency.currencyNetwork,
          walletProvider,
          walletKey: activeWallet.key,
        });
      }
      onConfirmSuccess();
    } catch (error) {
      const context = setupFutureUsage || isGasless ? 'setup' : 'transfer';
      await resyncSessionState();
      handleError({error, intl, context});
    } finally {
      setIsProcessing(false);
    }
  };

  return handlePayment;
};
